import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import type { UpgradePreview } from "@/react/types/Paywalls/Checkout/UpgradePreview";

import {
  upgradePriceComparison,
  upgradeSourceCycleAmount,
} from "./helpers/upgradePriceComparison";
import { UpgradePriceCallout } from "./UpgradePriceCallout";

const i18nRoot = "paywall_checkout.offer_page.upgrade";

type PriceOption = UpgradePreview["price_options"][number];

/** A single different-cadence price needs a description, not a selector. */
export const UpgradeSinglePriceDescription = ({
  priceOption,
}: {
  priceOption: PriceOption;
}) => {
  const comparison = upgradePriceComparison(priceOption);
  return (
    <Typography
      className="mt-1 flex flex-wrap items-baseline gap-1"
      variant="body-sm"
      color="secondary"
      data-testid="upgrade-price-options"
    >
      <span>{priceOption.billing_label}</span>
      <span className="mx-1">{t([i18nRoot, "cadence_term_separator"])}</span>
      {comparison.beforeAmount && (
        <s data-testid="upgrade-single-price-before-amount">
          {comparison.beforeAmount}
        </s>
      )}
      <span data-testid="upgrade-selected-list-price">{comparison.amount}</span>
      <span>{t([i18nRoot, "term_units", priceOption.interval_type])}</span>
    </Typography>
  );
};

export const UpgradeSinglePriceTerm = ({
  priceOption,
}: {
  priceOption: PriceOption;
}) => {
  const comparison = upgradePriceComparison(priceOption);
  return (
    <Typography
      className="inline-flex shrink-0 items-baseline gap-1"
      variant="body-md"
      data-testid="upgrade-single-same-cycle-price"
    >
      {comparison.beforeAmount && (
        <s
          className="text-light"
          data-testid="upgrade-single-price-before-amount"
        >
          {comparison.beforeAmount}
        </s>
      )}
      <strong data-testid="upgrade-selected-list-price">
        {comparison.amount}
      </strong>
      <span className="text-light">
        {t([i18nRoot, "term_units", priceOption.interval_type])}
      </span>
    </Typography>
  );
};

/** Cadence name with the admin's configured callout beside it. */
export const UpgradePriceOptionLabel = ({
  priceId,
  priceOption,
  priceTitle,
}: {
  priceId: number;
  priceOption: PriceOption;
  priceTitle: string;
}) => (
  <div className="flex flex-wrap items-baseline gap-1">
    <Typography variant="body-md">
      <strong>{priceOption.billing_label}</strong>
    </Typography>
    {priceTitle && (
      <Typography className="sr-only" variant="body-sm">
        {priceTitle}
      </Typography>
    )}
    <UpgradePriceCallout priceId={priceId} showSeparator />
  </div>
);

/**
 * Full-term price, e.g. `$384 $336 /yr`. The struck-through figure lives here and
 * only here, so the monthly figure on the right reads as a single price.
 */
export const UpgradePriceOptionTerm = ({
  isSelected,
  priceOption,
}: {
  isSelected: boolean;
  priceOption: PriceOption;
}) => {
  const comparison = upgradePriceComparison(priceOption);

  return (
    <Typography
      className="inline-flex items-baseline gap-1"
      component="span"
      variant="body-sm"
    >
      {comparison.beforeAmount && <s>{comparison.beforeAmount}</s>}
      <span
        data-testid={
          isSelected
            ? "upgrade-selected-list-price"
            : "upgrade-price-option-list-amount"
        }
      >
        {comparison.amount}
      </span>
      <span>{t([i18nRoot, "term_units", priceOption.interval_type])}</span>
    </Typography>
  );
};

/** Price per the current plan's billing cycle, bold with a muted unit. Never struck through. */
export const UpgradePriceOptionSourceCycle = ({
  priceOption,
  sourceIntervalType,
}: {
  priceOption: PriceOption;
  sourceIntervalType: PriceOption["interval_type"];
}) => {
  const amount = upgradeSourceCycleAmount(priceOption, sourceIntervalType);
  if (!amount) return null;

  return (
    <Typography
      className="inline-flex shrink-0 items-baseline gap-1"
      variant="body-md"
      data-testid="upgrade-price-option-source-cycle-amount"
    >
      <strong>{amount}</strong>
      <span className="text-light">
        {t([i18nRoot, "term_units", sourceIntervalType])}
      </span>
    </Typography>
  );
};
